import { useState, useCallback } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, Cell } from 'recharts';
import { ChartContainer } from './ChartContainer';
import { useTransactionSizeDistribution } from '../../hooks/useTransactionSizeDistribution';
import { formatMXN } from './constants';
import type { BinMetric, TransactionBin } from '../../utils/transactionBins';

const TOOLTIP_STYLE = {
  backgroundColor: 'white',
  border: '1px solid #e5e7eb',
  borderRadius: '0.375rem',
};

const TOOLTIP_WRAPPER_STYLE = {
  zIndex: 100,
};

interface TransactionSizeDistributionChartProps {
  height?: number;
  onBinClick?: (bin: TransactionBin) => void;
}

interface BarClickData {
  payload?: TransactionBin;
}

export function TransactionSizeDistributionChart({
  height = 350,
  onBinClick,
}: TransactionSizeDistributionChartProps) {
  const [metric, setMetric] = useState<BinMetric>('count');
  const { bins, maxCount } = useTransactionSizeDistribution();

  const hasData = maxCount > 0;
  const dataKey = metric === 'count' ? 'count' : 'totalValue';

  const handleBarClick = useCallback(
    (data: BarClickData) => {
      if (onBinClick && data.payload && data.payload.count > 0) {
        onBinClick(data.payload);
      }
    },
    [onBinClick]
  );

  return (
    <div>
      <div className="flex justify-end mb-4">
        <div className="inline-flex rounded-lg border border-gray-200 bg-gray-50 p-1">
          <button
            type="button"
            onClick={() => setMetric('count')}
            className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
              metric === 'count'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Frecuencia
          </button>
          <button
            type="button"
            onClick={() => setMetric('value')}
            className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
              metric === 'value'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Valor
          </button>
        </div>
      </div>
      {hasData ? (
        <ChartContainer height={height}>
          <BarChart
            data={bins}
            margin={{ top: 0, right: 0, left: 0, bottom: 0 }}
          >
            <XAxis dataKey="label" tick={{ fontSize: 12 }} />
            <YAxis
              tickFormatter={(v) =>
                metric === 'count' ? String(v) : `$${(v / 1000).toFixed(0)}k`
              }
            />
            <Tooltip
              formatter={(value) =>
                metric === 'count'
                  ? [`${value} transacciones`, 'Cantidad']
                  : [formatMXN(Number(value)), 'Total']
              }
              labelFormatter={(label) => `Rango: ${label}`}
              contentStyle={TOOLTIP_STYLE}
              wrapperStyle={TOOLTIP_WRAPPER_STYLE}
              cursor={false}
            />
            <Bar
              dataKey={dataKey}
              maxBarSize={60}
              onClick={onBinClick ? handleBarClick : undefined}
              style={{ cursor: onBinClick ? 'pointer' : 'default' }}
            >
              {bins.map((bin) => (
                <Cell key={bin.label} fill={bin.color} />
              ))}
            </Bar>
          </BarChart>
        </ChartContainer>
      ) : (
        <ChartContainer height={height}>
          <BarChart
            data={bins}
            margin={{ top: 0, right: 0, left: 0, bottom: 0 }}
          >
            <XAxis dataKey="label" tick={{ fontSize: 12 }} />
            <YAxis tickFormatter={() => '0'} />
            <Bar dataKey="count" maxBarSize={60}>
              {bins.map((bin) => (
                <Cell key={bin.label} fill={bin.color} />
              ))}
            </Bar>
          </BarChart>
        </ChartContainer>
      )}
    </div>
  );
}
